// WHICH ONE JOB a one-line surface names for a machine (#6745 review thread): the List
// view's Doing now cell and its sort, and `taskLine` (the card's status sentence, the
// panel head). ONE selector, so the line and its repo can never name two different jobs.
//
// The machine's `jobs[]` are oldest first and include rows that run nothing: a paused job
// and a resumed `pending` one pinned to the machine (#6748). Naming the oldest row would put
// a paused job in front of the work actually running, so the pick is the OLDEST RUNNING job
// (`assigned` / `in_progress`). With none running, it falls back to the oldest job, and the
// pick says it is not running so the surface can show the job's state beside its title.

import type { UtilBox, UtilJob } from './fleetUtilization';

/** A job running on its machine: claimed or started. Paused and pinned `pending` run nothing. */
export const isRunningJob = (job: Pick<UtilJob, 'status'>): boolean =>
  job.status === 'assigned' || job.status === 'in_progress';

/** The job a one-line surface names, and whether it is running. */
export type ShownJob = { readonly job: UtilJob; readonly running: boolean };

/** The oldest running job, else the oldest job; null when the machine lists none. */
export function shownJob(box: Pick<UtilBox, 'jobs'>): ShownJob | null {
  const jobs = box.jobs ?? [];
  const running = jobs.find(isRunningJob);
  if (running !== undefined) return { job: running, running: true };
  const oldest = jobs[0];
  return oldest === undefined ? null : { job: oldest, running: false };
}

/**
 * The state word shown beside a fallback (not running) job: Paused, Starting (a pinned
 * `pending` job waiting for its machine) or Last known. Null for a state that is a running
 * one, or one this bundle does not know.
 */
export function shownJobStateKey(job: Pick<UtilJob, 'state'>): string | null {
  switch (job.state) {
    case 'paused':
      return 'fleet.task_status_paused';
    case 'starting':
      return 'fleet.machines.job_tag_starting';
    case 'last_known':
      return 'fleet.machines.job_tag_last_known';
    default:
      return null;
  }
}
